import { useEffect } from 'react';
import { getSelector, type AnchoredSelector } from '../shared/selector';

export type PickResult = {
  selector: AnchoredSelector;
  rect: DOMRect;
  clickX: number;
  clickY: number;
};

export function PickerOverlay({
  onPick,
  onCancel,
}: {
  onPick: (target: PickResult) => void;
  onCancel: () => void;
}) {
  // Crosshair cursor on the document while picker is active. Restored on unmount.
  useEffect(() => {
    const root = document.documentElement;
    const prev = root.style.cursor;
    root.style.cursor = 'crosshair';
    return () => {
      root.style.cursor = prev;
    };
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (eventOriginatesInExtension(e)) return;
      e.preventDefault();
      e.stopPropagation();
      const target = elementFromEventIgnoringExtension(e);
      if (!target) return;
      const selector = getSelector(target, { clientX: e.clientX, clientY: e.clientY });
      onPick({
        selector,
        rect: target.getBoundingClientRect(),
        clickX: e.clientX + window.scrollX,
        clickY: e.clientY + window.scrollY,
      });
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };

    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [onPick, onCancel]);

  return (
    <div className="uin-picker-banner">
      Click anywhere on the page to drop a comment
      <button onClick={onCancel}>Cancel (Esc)</button>
    </div>
  );
}

function eventOriginatesInExtension(e: Event): boolean {
  const path = e.composedPath();
  for (const node of path) {
    if (node instanceof Element && node.id === 'ui-noter-shadow-host') return true;
  }
  return false;
}

function elementFromEventIgnoringExtension(e: MouseEvent): Element | null {
  const stack = document.elementsFromPoint(e.clientX, e.clientY);
  for (const el of stack) {
    if (el.id === 'ui-noter-shadow-host') continue;
    if (el === document.documentElement || el === document.body) continue;
    return el;
  }
  return null;
}
